import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Plus, Search, Trash2, Pencil, Play } from 'lucide-react';
import apiService from '@/services/api';

const Container = styled.div`
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
`;

const HelpText = styled.p`
  margin: 0 0 16px 0;
  color: #6b7280;
  font-size: 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 140px;
  gap: 12px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 12px 0;
`;

const Button = styled.button`
  padding: 10px 12px;
  border-radius: 6px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SecondaryButton = styled(Button)`
  background: #4b5563;
`;

const DangerButton = styled(Button)`
  background: #dc2626;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  th, td { border-bottom: 1px solid #e5e7eb; padding: 10px; text-align: left; vertical-align: top; }
  th { background: #f9fafb; font-weight: 600; font-size: 14px; }
`;

const ProductList = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const ProductRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr 110px;
  gap: 8px;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #e5e7eb;
  &:last-child { border-bottom: none; }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
`;

const Empty = styled.div`
  color: #6b7280;
  font-size: 14px;
  padding: 12px;
`;

type RequiredProduct = {
  product_control: number;
  product_name: string;
};

const emptyForm = {
  rim_size: '',
  description: '',
  active: true,
  products: [] as RequiredProduct[],
};

const TireAlertRules: React.FC = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState<any[]>([]);
  const [preview, setPreview] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);

  const carregar = async () => {
    const items = await apiService.listarRegrasAlertaPneus();
    setRules(items);
  };

  useEffect(() => {
    carregar().catch((e: any) => toast.error(e?.response?.data?.error || 'Falha ao carregar regras'));
  }, []);

  const limpar = () => {
    setEditingId(null);
    setForm(emptyForm);
    setProductQuery('');
    setProductResults([]);
  };

  const salvar = async () => {
    try {
      if (!form.rim_size.trim()) {
        toast.error('Informe o aro');
        return;
      }
      if (form.products.length === 0) {
        toast.error('Vincule pelo menos um produto obrigatório');
        return;
      }
      const payload = {
        ...form,
        rim_size: form.rim_size.replace(/\D/g, ''),
      };
      if (editingId) {
        await apiService.atualizarRegraAlertaPneus(editingId, payload);
      } else {
        await apiService.criarRegraAlertaPneus(payload);
      }
      toast.success('Regra salva');
      limpar();
      await carregar();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao salvar regra');
    }
  };

  const buscarProdutos = async () => {
    try {
      if (!productQuery.trim()) {
        toast.error('Digite um código ou descrição');
        return;
      }
      setLoading(true);
      const items = await apiService.buscarProdutosDepositoAlertaPneus(productQuery, 30);
      setProductResults(items);
      if (items.length === 0) toast('Nenhum produto encontrado no Depósito');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao buscar produtos');
    } finally {
      setLoading(false);
    }
  };

  const adicionarProduto = (produto: any) => {
    const controle = Number(produto.Controle);
    if (form.products.some((p) => Number(p.product_control) === controle)) {
      toast.error('Produto já vinculado a esta regra');
      return;
    }
    setForm((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        { product_control: controle, product_name: String(produto.Produto || '') },
      ],
    }));
  };

  const removerProduto = (controle: number) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.filter((p) => Number(p.product_control) !== Number(controle)),
    }));
  };

  const editar = (rule: any) => {
    setEditingId(Number(rule.id));
    setForm({
      rim_size: String(rule.rim_size || ''),
      description: rule.description || '',
      active: !!rule.active,
      products: (rule.products || []).map((p: any) => ({
        product_control: Number(p.product_control),
        product_name: String(p.product_name || ''),
      })),
    });
    setProductResults([]);
    setPreview(null);
  };

  const excluir = async (id: number) => {
    if (!confirm('Confirma excluir esta regra de aro?')) return;
    await apiService.excluirRegraAlertaPneus(id);
    toast.success('Regra excluída');
    await carregar();
  };

  const gerarPreview = async () => {
    try {
      setLoading(true);
      const result = await apiService.previewAlertaPneus(15);
      setPreview(result);
      toast.success(`Prévia concluída: ${result.alerts?.length || 0} alerta(s)`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao gerar prévia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <Title>Regras de Alerta de Pneus</Title>
        <HelpText>
          Configure quais produtos precisam acompanhar a venda de pneus por aro. Os produtos são pesquisados no cadastro do Depósito e os vínculos ficam salvos apenas no banco admin.
        </HelpText>

        <Grid>
          <Field>
            <Label>Aro</Label>
            <Input
              value={form.rim_size}
              onChange={(e) => setForm((prev) => ({ ...prev, rim_size: e.target.value.replace(/\D/g, '') }))}
              placeholder="14"
            />
          </Field>
          <Field>
            <Label>Descrição</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Ex.: Pneus aro 14"
            />
          </Field>
          <Field>
            <Label>Status</Label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 36 }}>
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
              />
              Ativa
            </label>
          </Field>
        </Grid>

        <div style={{ marginTop: 16 }}>
          <Label>Produtos obrigatórios</Label>
          <ProductList>
            {form.products.length === 0 ? (
              <Empty>Nenhum produto vinculado.</Empty>
            ) : (
              form.products.map((p) => (
                <ProductRow key={p.product_control}>
                  <Badge>{p.product_control}</Badge>
                  <div>{p.product_name}</div>
                  <DangerButton type="button" onClick={() => removerProduto(p.product_control)}>
                    <Trash2 size={14} /> Remover
                  </DangerButton>
                </ProductRow>
              ))
            )}
          </ProductList>
        </div>

        <div style={{ marginTop: 16 }}>
          <Label>Buscar produto no Depósito</Label>
          <Actions>
            <Input
              style={{ minWidth: 280 }}
              value={productQuery}
              onChange={(e) => setProductQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') buscarProdutos(); }}
              placeholder="Código ou descrição do produto"
            />
            <SecondaryButton type="button" onClick={buscarProdutos} disabled={loading}>
              <Search size={16} /> Buscar
            </SecondaryButton>
          </Actions>
          {productResults.length > 0 && (
            <ProductList>
              {productResults.map((p) => (
                <ProductRow key={p.Controle}>
                  <Badge>{p.Controle}</Badge>
                  <div>
                    <strong>{p.Produto}</strong>
                    <div style={{ color: '#6b7280', fontSize: 12 }}>
                      {p.Grupo || 'Sem grupo'} {p.Medida ? `| ${p.Medida}` : ''}
                    </div>
                  </div>
                  <Button type="button" onClick={() => adicionarProduto(p)}>
                    <Plus size={14} /> Vincular
                  </Button>
                </ProductRow>
              ))}
            </ProductList>
          )}
        </div>

        <Actions>
          <Button type="button" onClick={salvar}>
            <Plus size={16} /> {editingId ? 'Atualizar regra' : 'Salvar regra'}
          </Button>
          <SecondaryButton type="button" onClick={limpar}>Novo</SecondaryButton>
          <SecondaryButton type="button" onClick={gerarPreview} disabled={loading}>
            <Play size={16} /> Prévia últimos 15 minutos
          </SecondaryButton>
        </Actions>

        {preview && (
          <div style={{ marginTop: 16, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <strong>Prévia:</strong> {preview.alerts?.length || 0} alerta(s), {preview.errors?.length || 0} filial(is) com erro.
            {(preview.alerts || []).slice(0, 5).map((alert: any, index: number) => (
              <div key={`${alert.filialId}-${alert.numero}-${alert.rimSize}-${index}`} style={{ marginTop: 8, fontSize: 13 }}>
                {alert.filialNome} | NC {alert.numero} | Aro {alert.rimSize} | Faltando: {(alert.missingProducts || []).map((p: any) => p.product_name).join(', ')}
              </div>
            ))}
          </div>
        )}

        <Table>
          <thead>
            <tr>
              <th>Aro</th>
              <th>Descrição</th>
              <th>Ativa</th>
              <th>Produtos vinculados</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td><Badge>{rule.rim_size}</Badge></td>
                <td>{rule.description || '-'}</td>
                <td>{rule.active ? 'Sim' : 'Não'}</td>
                <td>{(rule.products || []).map((p: any) => p.product_name).join(', ') || '-'}</td>
                <td>
                  <Actions style={{ margin: 0 }}>
                    <SecondaryButton type="button" onClick={() => editar(rule)}>
                      <Pencil size={14} /> Editar
                    </SecondaryButton>
                    <DangerButton type="button" onClick={() => excluir(rule.id)}>
                      <Trash2 size={14} /> Excluir
                    </DangerButton>
                  </Actions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
};

export default TireAlertRules;
